// frontend/src/services/api.ts
// Updated to align with backend API documentation
// Changes:
// - Switched to query-based presigned URLs for single files (/api/presigned/?key=...) to match doc
// - Kept path-based as optional fallback since backend supports it
// - Added support for advanced search POST
// - Added sorting and pagination params to file listing functions
// - Updated search to use /api/search/files for basic search
// - Added /api/search/prefix for prefix search
// - Enhanced error handling with more specific codes
// - Added folder structure and breadcrumbs functions
// - Improved logging for cache and errors
// - Added global params like presignedUrls where applicable

import axios, { AxiosError, AxiosResponse } from 'axios';

// ============================================================================
// Logging Service
// ============================================================================

export const apiLogger = {
  error: (context: string, error: any, details?: any) => {
    console.error(`[API Error] ${context}:`, error, details || '');
  },
  warn: (context: string, message: string, details?: any) => {
    console.warn(`[API Warning] ${context}: ${message}`, details || '');
  },
  info: (context: string, message: string, details?: any) => {
    console.info(`[API Info] ${context}: ${message}`, details || '');
  }
};

// ============================================================================
// Error Handling
// ============================================================================

export class ApiError extends Error {
  public readonly status?: number;
  public readonly code?: string;
  public readonly context: string;
  public readonly originalError: any;

  constructor(message: string, context: string, originalError?: any, status?: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.context = context;
    this.originalError = originalError;
    this.status = status;
    this.code = code;
  }
}

const handleApiError = (error: any, context: string): ApiError => {
  let message: string;
  let status: number | undefined;
  let errorCode: string | undefined;

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    status = axiosError.response?.status;
    
    if (status === 404) {
      message = 'The requested resource was not found';
      errorCode = 'NOT_FOUND';
    } else if (status === 401) {
      message = 'Authentication required';
      errorCode = 'UNAUTHORIZED';
    } else if (status === 403) {
      message = 'You do not have permission to access this resource';
      errorCode = 'FORBIDDEN';
    } else if (status === 500) {
      message = 'An internal server error occurred';
      errorCode = 'SERVER_ERROR';
    } else if (status === 503 || status === 504) {
      message = 'The server is currently unavailable or timed out';
      errorCode = 'SERVICE_UNAVAILABLE';
    } else if (axiosError.code === 'ECONNABORTED') {
      message = 'The request timed out';
      errorCode = 'TIMEOUT';
    } else if (axiosError.code === 'ERR_NETWORK') {
      message = 'Network error - please check your connection';
      errorCode = 'NETWORK_ERROR';
    } else {
      message = axiosError.message || 'An unknown API error occurred';
      errorCode = 'UNKNOWN_ERROR';
    }
  } else if (error instanceof Error) {
    message = error.message;
    errorCode = 'CLIENT_ERROR';
  } else {
    message = String(error);
    errorCode = 'UNKNOWN_ERROR';
  }

  apiLogger.error(context, message, { status, errorCode, originalError: error });
  return new ApiError(message, context, error, status, errorCode);
};

const shouldRetryError = (error: any): boolean => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status && status >= 400 && status < 500) {
      return status === 429;
    }
    return (status && status >= 500) || 
           error.code === 'ECONNABORTED' || 
           error.code === 'ERR_NETWORK';
  }
  return false;
};

const robustApiCall = async <T>(
  apiCall: () => Promise<AxiosResponse<T>>,
  context: string,
  retries: number = 3,
  delay: number = 1000
): Promise<T> => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await apiCall();
      return response.data;
    } catch (error) {
      if (i === retries - 1 || !shouldRetryError(error)) {
        throw handleApiError(error, context);
      }
      apiLogger.warn(context, `Retry ${i + 1}/${retries} after error: ${error instanceof Error ? error.message : String(error)}`);
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
  throw new ApiError(`Retry limit reached (${retries})`, context);
};

// Base URL setup
// No hardcoded fallback — if REACT_APP_API_URL is not set, use relative URLs ('').
// In production, Cloudflare routes /api/* to the Worker on the same domain (no CORS).
// In local dev, set REACT_APP_API_URL=http://127.0.0.1:8787 in .env.local.
const cleanBaseUrl = (url: string | undefined): string => {
  if (!url) return '';
  return url.trim().replace(/\/+$/, '');
};

export const baseURL = cleanBaseUrl(process.env.REACT_APP_API_URL);
apiLogger.info('Config', `Using API URL: ${baseURL}`);

export const axiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  withCredentials: true,
});

// Debug interceptors
if (process.env.NODE_ENV !== 'production') {
  axiosInstance.interceptors.request.use(request => {
    apiLogger.info('Request', `${request.method?.toUpperCase() || 'GET'} ${request.url}`, {
      params: request.params,
      data: request.data
    });
    return request;
  });

  axiosInstance.interceptors.response.use(
    response => {
      apiLogger.info('Response', `${response.status} ${response.config.url}`, {
        headers: response.headers,
        dataSize: JSON.stringify(response.data).length
      });
      return response;
    },
    error => {
      apiLogger.error('Response', `Failed ${error.config?.method?.toUpperCase() || 'GET'} ${error.config?.url}`, error);
      return Promise.reject(error);
    }
  );
}

// ============================================================================
// Response Types and Interfaces
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
  meta?: { cacheHit?: boolean; totalCount?: number; nextCursor?: string };
}

export enum FileType {
  VIDEO = 'video',
  CSV = 'csv',
  TEXT = 'text',
  AUDIO = 'audio',
  IMAGE = 'image',
  DOCUMENT = 'document',
  OTHER = 'other'
}

export interface FileMetadata {
  key: string;
  size: number;
  lastModified: string | null;
  fileType: FileType | string;
  mimeType: string;
  contentLength: number;
  isViewable?: boolean;
  filename?: string;
  folder?: string;
  presignedUrl?: string;
  downloadUrl?: string;
  metadata?: Record<string, any>;
}

export interface FileListResponse {
  success: boolean;
  files: FileMetadata[];
  hasMore?: boolean;
  nextContinuationToken?: string | null;
  totalCount?: number;
}

export interface PresignedUrlResponse {
  success: boolean;
  url: string;
  filename: string;
  expiresAt?: string;
  fileType?: FileType | string;
  mimeType?: string;
  isViewable?: boolean;
  cached?: boolean;
  data?: string;
}

export interface BatchPresignedUrlResponse {
  success: boolean;
  urls: {
    key: string;
    url: string | null;
    fileType?: FileType | string;
    mimeType?: string;
    filename?: string;
    cacheControl?: string;
    error?: string;
  }[];
  expiresAt: string;
}

export interface Folder {
  name: string;
  path: string;
  type: 'folder';
  lastModified?: string | null;
  metadata?: Record<string, any>;
}

export interface FolderFile {
  name: string;
  key: string;
  size: number;
  lastModified: string | null;
  type: 'file';
  fileType?: FileType | string;
  mimeType?: string;
  isViewable?: boolean;
  metadata?: Record<string, any>;
  presignedUrl?: string;
  downloadUrl?: string;
}

export interface Breadcrumb {
  name: string;
  path: string;
}

export interface FolderContentsResponse {
  success: boolean;
  folders: Folder[];
  files: FolderFile[];
  commonPrefix: string;
  nextContinuationToken?: string;
  isTruncated: boolean;
  breadcrumbs: Breadcrumb[];
  path?: string;
  parentPath?: string;
  totalItems?: number;
}

export interface AdvancedSearchParams {
  searchText?: string;
  fileType?: FileType | string | (FileType | string)[];
  minSize?: number;
  maxSize?: number;
  fromDate?: string;
  toDate?: string;
  extensions?: string | string[];
  exactMatch?: boolean;
  sortBy?: 'name' | 'date' | 'size' | string;
  sortDirection?: 'asc' | 'desc' | string;
}

export interface FolderStructureResponse {
  path: string;
  name: string;
  subfolders: FolderStructureResponse[];
}

export interface CacheDurations {
  cloudflare: {
    video: number;
    image: number;
    audio: number;
    document: number;
    csv: number;
    text: number;
    other: number;
  };
  presignedUrl: {
    view: number;
    download: number;
  };
  apiResponse: {
    fileList: number;
    folderList: number;
    searchResults: number;
    recentFiles: number;
  };
  workerCache: {
    presignedUrls: number;
    fileMetadata: number;
  };
}

// Presigned URL cache — backed by sessionStorage so it survives page refreshes
// within the same browser tab. sessionStorage is cleared when the tab closes,
// preventing stale presigned URLs (which expire after 2–4 hours) from persisting
// across sessions the way localStorage would.
type CachedUrl = { response: PresignedUrlResponse; expiresAt: number };

const SESSION_CACHE_KEY = 'presigned_url_cache';

function loadCache(): Record<string, CachedUrl> {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, CachedUrl>;
    const now = Date.now();
    // Evict expired entries on load to keep sessionStorage lean
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => v.expiresAt > now)
    );
  } catch {
    return {};
  }
}

function saveCache(cache: Record<string, CachedUrl>): void {
  try {
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // sessionStorage can throw in private browsing or when storage quota is exceeded.
    // Silently ignore — in-memory cache still works.
  }
}

let presignedUrlCache = loadCache();

setInterval(() => {
  const now = Date.now();
  let expiredCount = 0;
  Object.keys(presignedUrlCache).forEach(key => {
    if (presignedUrlCache[key].expiresAt < now) {
      delete presignedUrlCache[key];
      expiredCount++;
    }
  });
  if (expiredCount > 0) {
    apiLogger.info('Cache', `Cleared ${expiredCount} expired URL cache entries`);
    saveCache(presignedUrlCache); // keep sessionStorage in sync after eviction
  }
}, 15 * 60 * 1000);

// ============================================================================
// API Methods
// ============================================================================

export const fetchCacheConfig = async (): Promise<CacheDurations | null> => {
  const context = 'fetchCacheConfig';
  try {
    const response = await robustApiCall(
      () => axiosInstance.get<CacheDurations>('/api/config/cache'),
      context
    );
    apiLogger.info(context, 'Successfully fetched cache configuration');
    return response;
  } catch (error) {
    apiLogger.error(context, 'Failed to fetch cache configuration', error);
    return null;
  }
};

export const fetchAllFiles = async (
  includeFolders: boolean = false,
  presignedUrls: boolean = false,
  cacheTime?: number
): Promise<FileListResponse> => {
  const context = 'fetchAllFiles';
  try {
    return await robustApiCall(
      () => axiosInstance.get<FileListResponse>('/api/listing/all', {
        params: { includeFolders, presignedUrls, ...(cacheTime ? { cacheTime } : {}) }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const searchAllFiles = async (
  searchText: string,
  sortBy?: 'name' | 'date' | 'size' | string,
  sortDirection?: 'asc' | 'desc' | string,
  cacheTime?: number
): Promise<FileListResponse> => {
  const context = 'searchAllFiles';
  try {
    return await robustApiCall(
      () => axiosInstance.get<FileListResponse>('/api/search/files', {
        params: { 
          searchText,
          ...(sortBy ? { sortBy } : {}),
          ...(sortDirection ? { sortDirection } : {}),
          ...(cacheTime ? { cacheTime } : {})
        }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const advancedSearch = async (
  params: AdvancedSearchParams,
  cacheTime?: number
): Promise<FileListResponse> => {
  const context = 'advancedSearch';
  try {
    return await robustApiCall(
      () => axiosInstance.post<FileListResponse>('/api/search/advanced', params, {
        params: { ...(cacheTime ? { cacheTime } : {}) }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const searchByPrefix = async (
  prefix: string,
  sortBy?: 'name' | 'date' | 'size' | string,
  sortDirection?: 'asc' | 'desc' | string,
  cacheTime?: number
): Promise<FileListResponse> => {
  const context = 'searchByPrefix';
  try {
    return await robustApiCall(
      () => axiosInstance.get<FileListResponse>('/api/search/prefix', {
        params: { 
          prefix,
          ...(sortBy ? { sortBy } : {}),
          ...(sortDirection ? { sortDirection } : {}),
          ...(cacheTime ? { cacheTime } : {})
        }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const fetchRecentFiles = async (
  limit: number = 10,
  extension: string = '.mp4',
  cacheTime?: number
): Promise<FileListResponse> => {
  const context = 'fetchRecentFiles';
  try {
    return await robustApiCall(
      () => axiosInstance.get<FileListResponse>('/api/listing/recent', {
        params: { limit, extension, ...(cacheTime ? { cacheTime } : {}) }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const searchFiles = async (
  searchText: string,
  page: number = 1,
  limit: number = 10,
  sortBy?: 'name' | 'date' | 'size' | string,
  sortDirection?: 'asc' | 'desc' | string,
  continuationToken?: string,
  cacheTime?: number
): Promise<FileListResponse> => {
  const context = 'searchFiles';
  try {
    return await robustApiCall(
      () => axiosInstance.get<FileListResponse>('/api/search/files', {
        params: {
          searchText,
          page,
          limit,
          ...(sortBy ? { sortBy } : {}),
          ...(sortDirection ? { sortDirection } : {}),
          ...(continuationToken ? { continuationToken } : {}),
          ...(cacheTime ? { cacheTime } : {})
        }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const getSortedFiles = async (
  sortBy: 'name' | 'date' | 'size' | string = 'name',
  sortDirection: 'asc' | 'desc' | string = 'asc',
  presignedUrls: boolean = false,
  cacheTime?: number
): Promise<FileListResponse> => {
  const context = 'getSortedFiles';
  try {
    return await robustApiCall(
      () => axiosInstance.get<FileListResponse>('/api/listing/sorted', {
        params: { sortBy, sortDirection, presignedUrls, ...(cacheTime ? { cacheTime } : {}) }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const getPaginatedFiles = async (
  page: number = 1,
  pageSize: number = 20,
  sortBy: 'name' | 'date' | 'size' | string = 'name',
  sortDirection: 'asc' | 'desc' | string = 'asc',
  presignedUrls: boolean = false,
  cacheTime?: number
): Promise<FileListResponse> => {
  const context = 'getPaginatedFiles';
  try {
    return await robustApiCall(
      () => axiosInstance.get<FileListResponse>('/api/listing/paged', {
        params: { page, pageSize, sortBy, sortDirection, presignedUrls, ...(cacheTime ? { cacheTime } : {}) }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const getPresignedViewUrl = async (
  key: string, 
  expiresIn?: number, 
  options: { cacheBuster?: number; forceRefresh?: boolean } = {}
): Promise<PresignedUrlResponse> => {
  const context = 'getPresignedViewUrl';
  const cacheKey = `view:${key}:${expiresIn || 'default'}:${options.cacheBuster || 'default'}`;
  const now = Date.now();
  const cached = presignedUrlCache[cacheKey];

  // forceRefresh bypasses the cache entirely. The HTML5 <video> player can hold a
  // presigned URL that S3 has already rejected with a 403 (the link expired between
  // the cache check and playback). When that happens the caller passes forceRefresh
  // to discard the stale entry and fetch a fresh, signed URL from the Worker.
  if (options.forceRefresh) {
    apiLogger.info(context, `Force-refreshing presigned URL for ${key} (bypassing cache)`);
    delete presignedUrlCache[cacheKey];
    saveCache(presignedUrlCache);
  } else if (cached && cached.expiresAt > now + 5 * 60 * 1000) {
    apiLogger.info(context, `Using cached presigned URL for ${key}`);
    return cached.response;
  }

  try {
    const params: Record<string, any> = { key, ...(expiresIn ? { expiresIn } : {}) };
    const response = await robustApiCall(
      () => axiosInstance.get<PresignedUrlResponse>('/api/presigned/', {
        params
      }),
      context
    );

    if (!response.url) {
      throw new ApiError('No URL found in response', context);
    }

    const filename = response.filename || key.split('/').pop() || key;
    const responseExpiry = response.expiresAt ? new Date(response.expiresAt).getTime() : null;
    const expiresAt = responseExpiry || (now + (expiresIn || 7200) * 1000);

    const result: PresignedUrlResponse = { 
      success: true,
      url: response.url, 
      filename,
      expiresAt: new Date(expiresAt).toISOString(),
      fileType: response.fileType,
      mimeType: response.mimeType,
      isViewable: response.isViewable,
      cached: response.cached
    };
    
    presignedUrlCache[cacheKey] = { response: result, expiresAt };
    saveCache(presignedUrlCache);
    return result;
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const getPresignedDownloadUrl = async (
  key: string, 
  expiresIn?: number
): Promise<PresignedUrlResponse> => {
  const context = 'getPresignedDownloadUrl';
  const cacheKey = `download:${key}:${expiresIn || 'default'}`;
  const now = Date.now();
  const cached = presignedUrlCache[cacheKey];

  // Return cached URL if it has more than 5 minutes of validity remaining
  if (cached && cached.expiresAt > now + 5 * 60 * 1000) {
    apiLogger.info(context, `Using cached download URL for ${key}`);
    return cached.response;
  }

  try {
    const params: Record<string, any> = { key, download: true, ...(expiresIn ? { expiresIn } : {}) };

    const response = await robustApiCall(
      () => axiosInstance.get<PresignedUrlResponse>('/api/presigned/', { params }),
      context
    );

    if (!response.url) {
      throw new ApiError('No URL found in response', context);
    }

    const responseExpiry = response.expiresAt ? new Date(response.expiresAt).getTime() : null;
    const expiresAt = responseExpiry || (now + (expiresIn || 14400) * 1000);

    const result: PresignedUrlResponse = {
      success: true,
      url: response.url,
      filename: response.filename || key.split('/').pop() || key,
      expiresAt: new Date(expiresAt).toISOString(),
      fileType: response.fileType,
      mimeType: response.mimeType,
      isViewable: response.isViewable,
      cached: response.cached,
    };

    presignedUrlCache[cacheKey] = { response: result, expiresAt };
    saveCache(presignedUrlCache);
    return result;
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const getBatchPresignedUrls = async (
  keys: string[], 
  expiresIn?: number, 
  download: boolean = false
): Promise<BatchPresignedUrlResponse> => {
  const context = 'getBatchPresignedUrls';
  try {
    const response = await robustApiCall(
      () => axiosInstance.post<BatchPresignedUrlResponse>('/api/presigned/batch', 
        { keys },
        { params: { ...(expiresIn ? { expiresIn } : {}), ...(download ? { download: true } : {}) } }
      ),
      context
    );

    const expiresAt = new Date(response.expiresAt).getTime();
    response.urls.forEach(item => {
      if (item.url) {
        const cacheKey = `view:${item.key}:${expiresIn || 'default'}`;
        presignedUrlCache[cacheKey] = {
          response: {
            success: true,
            url: item.url,
            filename: item.filename || item.key.split('/').pop() || item.key,
            fileType: item.fileType,
            mimeType: item.mimeType,
            expiresAt: response.expiresAt
          },
          expiresAt
        };
      }
    });

    return response;
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const getUrlCacheStats = async (cacheTime?: number): Promise<any> => {
  const context = 'getUrlCacheStats';
  try {
    return await robustApiCall(
      () => axiosInstance.get('/api/presigned/stats', {
        params: { ...(cacheTime ? { cacheTime } : {}) }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

export const fetchCsvContent = async (key: string, cacheTime?: number): Promise<PresignedUrlResponse> => {
  const context = 'fetchCsvContent';
  try {
    // Proxy through backend to avoid S3 CORS restrictions on direct fetches
    const response = await axiosInstance.get<string>('/api/content', {
      params: { key },
      responseType: 'text',
    });
    return {
      url: '',
      success: true,
      data: response.data,
      filename: key.split('/').pop() || key,
    };
  } catch (error) {
    apiLogger.error(context, `Error fetching CSV data: ${error}`, error);
    throw handleApiError(error, context);
  }
};

export const fetchTextContent = async (key: string, cacheTime?: number): Promise<PresignedUrlResponse> => {
  const context = 'fetchTextContent';
  try {
    // Proxy through backend to avoid S3 CORS restrictions on direct fetches
    const response = await axiosInstance.get<string>('/api/content', {
      params: { key },
      responseType: 'text',
    });
    return {
      url: '',
      success: true,
      data: response.data,
      filename: key.split('/').pop() || key,
    };
  } catch (error) {
    apiLogger.error(context, `Error fetching text data: ${error}`, error);
    throw handleApiError(error, context);
  }
};

// In api.ts
export const getFileMetadata = async (key: string, cacheTime?: number): Promise<{ success: boolean; metadata: FileMetadata }> => {
  const context = 'getFileMetadata';
  try {
    console.log('[Debug] Fetching metadata for key:', key); // Add debug log
    return await robustApiCall(
      () => axiosInstance.get<{ success: boolean; metadata: FileMetadata }>('/api/s3/metadata', {
        params: { key, ...(cacheTime ? { cacheTime } : {}) } // Pass key directly, no encodeURIComponent
      }),
      context
    );
  } catch (error) {
    console.error('[Debug] Metadata fetch error:', error); // Add debug log
    throw handleApiError(error, context);
  }
};

export const streamFile = async (
  key: string, 
  download: boolean = false,
  json: boolean = false,
  cacheTime?: number
): Promise<PresignedUrlResponse> => {
  const context = 'streamFile';
  try {
    return await robustApiCall(
      () => axiosInstance.get<PresignedUrlResponse>('/api/stream/', {
        params: {
          key,
          ...(download ? { download: true } : {}),
          ...(json ? { json: true } : {}),
          ...(cacheTime ? { cacheTime } : {})
        }
      }),
      context
    );
  } catch (error) {
    throw handleApiError(error, context);
  }
};

// ============================================================================
// Folder API Methods
// ============================================================================

const deriveLastModifiedForFolder = (files: FolderFile[], folderPath: string): string | null => {
  const folderFiles = files.filter(file => 
    file.key.startsWith(folderPath + '/') && 
    file.key.split('/').length === folderPath.split('/').length + 1
  );
  if (folderFiles.length === 0) return null;
  return folderFiles.reduce((latestDate, file) => {
    const currentFileDate = file.lastModified;
    if (!latestDate) return currentFileDate;
    if (!currentFileDate) return latestDate;
    return new Date(currentFileDate) > new Date(latestDate) ? currentFileDate : latestDate;
  }, null as string | null);
};

export const fetchRootFolders = async (
  presignedUrls: boolean = true,
  cacheTime?: number
): Promise<FolderContentsResponse> => {
  const context = 'fetchRootFolders';
  apiLogger.info(context, 'Fetching root folders');
  try {
    const response = await robustApiCall(
      () => axiosInstance.get<FolderContentsResponse>('/api/folders', {
        params: { presignedUrls, ...(cacheTime ? { cacheTime } : {}) }
      }),
      context
    );
    apiLogger.info(context, `Received ${response.folders.length} folders and ${response.files.length} files`);
    return response;
  } catch (error) {
    apiLogger.error(context, 'Failed to fetch root folders', error);
    return {
      success: false,
      folders: [],
      files: [],
      commonPrefix: '',
      isTruncated: false,
      breadcrumbs: [{ name: 'Root', path: '' }]
    };
  }
};

export const fetchFolderContents = async (
  folderPath: string,
  presignedUrls: boolean = true,
  maxKeys: number = 1000,
  cacheTime?: number,
  continuationToken?: string
): Promise<FolderContentsResponse> => {
  const context = 'fetchFolderContents';
  apiLogger.info(context, `Fetching folder contents for path: ${folderPath}`);
  try {
    const response = await robustApiCall(
      () => axiosInstance.get<FolderContentsResponse>('/api/folders/contents', {
        params: {
          path: folderPath,
          presignedUrls,
          maxKeys,
          ...(cacheTime ? { cacheTime } : {}),
          ...(continuationToken ? { continuationToken } : {})
        }
      }),
      context
    );
    const enhancedFiles = response.files.map(file => ({
      ...file,
      name: file.name || getFileName(file.key),
      type: 'file' as const
    }));
    const foldersWithModifiedDate = response.folders.map(folder => ({
      ...folder,
      lastModified: deriveLastModifiedForFolder(enhancedFiles, folder.path)
    }));
    apiLogger.info(context, `Received ${response.folders.length} folders and ${response.files.length} files`);
    return { ...response, files: enhancedFiles, folders: foldersWithModifiedDate };
  } catch (error) {
    apiLogger.error(context, 'Failed to fetch folder contents', error);
    return {
      success: false,
      folders: [],
      files: [],
      commonPrefix: folderPath,
      isTruncated: false,
      breadcrumbs: [
        { name: 'Root', path: '' },
        { name: folderPath.split('/').pop() || folderPath, path: folderPath }
      ]
    };
  }
};

export const getFolderBreadcrumbs = async (
  folderPath: string,
  cacheTime?: number
): Promise<{ breadcrumbs: Breadcrumb[] }> => {
  const context = 'getFolderBreadcrumbs';
  apiLogger.info(context, `Fetching breadcrumbs for path: ${folderPath}`);
  try {
    const response = await robustApiCall(
      () => axiosInstance.get<{ success: boolean; breadcrumbs: Breadcrumb[] }>(
        '/api/folders/breadcrumbs', {
          params: { path: folderPath, ...(cacheTime ? { cacheTime } : {}) }
        }
      ),
      context
    );
    return { breadcrumbs: response.breadcrumbs };
  } catch (error) {
    apiLogger.error(context, 'Failed to fetch breadcrumbs', error);
    return {
      breadcrumbs: [
        { name: 'Root', path: '' },
        { name: folderPath.split('/').pop() || folderPath, path: folderPath }
      ]
    };
  }
};

export const getRecentFolders = async (
  limit: number = 5,
  cacheTime?: number
): Promise<Folder[]> => {
  const context = 'getRecentFolders';
  try {
    const response = await robustApiCall(
      () => axiosInstance.get<{ folders: Folder[] }>('/api/folders/recent', {
        params: { limit, ...(cacheTime ? { cacheTime } : {}) }
      }),
      context
    );
    return response.folders;
  } catch (error) {
    apiLogger.error(context, 'Failed to fetch recent folders', error);
    return [];
  }
};

export const getFolderStructure = async (
  path: string = '',
  depth: number = 2,
  cacheTime?: number
): Promise<FolderStructureResponse> => {
  const context = 'getFolderStructure';
  try {
    const response = await robustApiCall(
      () => axiosInstance.get<FolderStructureResponse>('/api/folders/structure', {
        params: { path, depth, ...(cacheTime ? { cacheTime } : {}) }
      }),
      context
    );
    return response;
  } catch (error) {
    apiLogger.error(context, 'Failed to fetch folder structure', error);
    return {
      path: path || '',
      name: path ? path.split('/').pop() || 'Folder' : 'Root',
      subfolders: []
    };
  }
};

export const getStreamUrl = (key: string, download: boolean = false): string => {
  const context = 'getStreamUrl';
  try {
    const timestamp = Date.now();
    const url = `/api/stream/${encodeURIComponent(key)}`;
    apiLogger.info(context, `Generating ${download ? 'download' : 'stream'} URL for ${key}`);
    if (download) {
      return `${baseURL}${url}?download=true&presigned=true&t=${timestamp}`;
    } else {
      return `${baseURL}${url}`;
    }
  } catch (error) {
    apiLogger.error(context, `Error generating URL for ${key}`, error);
    if (download) {
      return `${baseURL}/api/stream/${key}?download=true&presigned=true`;
    } else {
      return `${baseURL}/api/stream/${key}`;
    }
  }
};

// ============================================================================
// Utility Functions
// ============================================================================

export const getFileName = (key: string): string => {
  try {
    return key.split('/').pop() || key;
  } catch (error) {
    apiLogger.warn('getFileName', `Could not extract filename from key: ${key}`, error);
    return key;
  }
};

export const formatFileSize = (bytes: number): string => {
  try {
    if (isNaN(bytes) || bytes < 0) throw new Error('Invalid byte count');
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const sizeIndex = Math.min(i, sizes.length - 1);
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[sizeIndex];
  } catch (error) {
    apiLogger.warn('formatFileSize', `Error formatting file size: ${bytes}`, error);
    return 'Unknown size';
  }
};